import type { ApiMessage } from '../api/types';

import { MESSAGES_IDB_STORE } from './browser/idb';
import { ACCOUNT_SLOT } from './multiaccount';

// Persisted history for one chat's main thread. `ids` is ordered oldest → newest and is the source of
// truth for what the chat knows; `byId` holds the bodies for exactly those ids.
export type StoredChatMessages = {
  ids: number[];
  byId: Record<number, ApiMessage>;
};

// Account slots each keep their own history, mirroring how the media caches are partitioned. Without
// the suffix, switching accounts would read the previous account's messages out of the store.
function buildKey(chatId: string) {
  return ACCOUNT_SLOT ? `${ACCOUNT_SLOT}:${chatId}` : chatId;
}

export function saveChatMessages(chatId: string, data: StoredChatMessages) {
  return MESSAGES_IDB_STORE.set(buildKey(chatId), data);
}

export function loadChatMessages(chatId: string) {
  return MESSAGES_IDB_STORE.get<StoredChatMessages>(buildKey(chatId));
}

// Drop chats that are no longer worth keeping. Called with the set that was just persisted, so the
// store never holds more than `MESSAGE_STORE_CHAT_LIMIT` chats per account — an unbounded store would
// eventually push the origin over its storage quota, which costs the whole cache, not just this part.
export async function pruneMessageStore(persistedChatIds: readonly string[]) {
  const keep = new Set(persistedChatIds.map(buildKey));
  const prefix = ACCOUNT_SLOT ? `${ACCOUNT_SLOT}:` : undefined;

  const staleKeys = (await MESSAGES_IDB_STORE.keys())
    .filter((key): key is string => typeof key === 'string')
    // Only this account's entries: another slot's keys are pruned when that slot is running
    .filter((key) => (prefix ? key.startsWith(prefix) : !key.includes(':')))
    .filter((key) => !keep.has(key));

  if (!staleKeys.length) return;

  await MESSAGES_IDB_STORE.delMany(staleKeys);
}

// Signing out of one slot must not touch the others. Clearing the whole store destroyed every other
// signed-in account's cached history too, so those chats opened blank until the network answered —
// the exact regression this per-chat store was built to remove.
export async function clearMessageStore() {
  const prefix = ACCOUNT_SLOT ? `${ACCOUNT_SLOT}:` : undefined;

  const ownKeys = (await MESSAGES_IDB_STORE.keys())
    .filter((key): key is string => typeof key === 'string')
    .filter((key) => (prefix ? key.startsWith(prefix) : !key.includes(':')));

  if (!ownKeys.length) return;

  await MESSAGES_IDB_STORE.delMany(ownKeys);
}
